/**
 * @author omriha
 * 05/02/2020
 */
import React from 'react';
import {connect} from 'react-redux';
import {push} from 'connected-react-router';
import {useTranslation} from 'react-i18next';
import queryString from 'query-string';
import SMCPage from '../../../components/core/pageComponents/SMCPage';
import ButtonContainer from '../../../components/Simple/ButtonContainer';
import {useEditorDimensions} from '../../../utils/hooks';
import LabelContainer from '../../../components/Page/LabelContainer';
import SMCEditor from '../../../components/Editor';
import SMCButton from '../../../components/Simple/SMCButton';
import {useFirmwareEditor} from './FirmwareEditorHelper';
import RouteLeavingGuard from '../../PopupPages/RouteLeavingGuard';
import {isAdminReadOnlySelector} from '../../../store/selectors/user.selectors';
import {SMCDisabledProvider} from '../../../components/Simple/SMCDisabledContext';
import VerifyButtonsContainer from "../VerifyButtonsContainer";
import ButtonsContainerStyles from "../ButtonsContainerStyles";
import lineSeparator from "../../../assets/images/Line.png";

/**
 * High level element to fetch, edit and save firmware, all logic implemented inside.
 * @param {string} id Id of firmware to fetch from the server to edit
 */
function FirmwareEditor({
    navigateTo,
    urlParams,
    id,
    isAdminReadOnly
}) {
    const { t } = useTranslation();
    const { editorWidth, editorHeight } = useEditorDimensions();
    const classes = ButtonsContainerStyles();

    let defaultPage = 'general';
    const queryParams = queryString.parse(urlParams);
    if (queryParams && queryParams['page']) {
        defaultPage = queryParams['page'];
    }

    const {
        firmwareObject,
        isObjectDirty,
        editorPages,
        helpId,
        isPageDataLoaded,
        isPageLoadFailed,
        chosenPageId,
        onPageChange,
        setFirmwareData,
        saveButtonOnClick,
        deleteButtonOnClick,
        revertButtonOnClick,
        reloadPageData,
        changeFirmwareOnAction,
        isPrivateFirnware,
        changeFirmwareToRecommendedAction,
        isRecommendedFirmware
    } = useFirmwareEditor(id, defaultPage);
    // Data passed down to the editor pages
    const editorPageData = {
        firmwareObject,
        setEditorPageId: onPageChange,
        setFirmwareData,
        isPageDataLoaded,
    }

    const displayChangeToGaAction = isPrivateFirnware;
    const displayRecommendedAction = !isRecommendedFirmware && !isPrivateFirnware;

    return (
        <>
        <RouteLeavingGuard when={isObjectDirty} />
        <SMCPage loading={!isPageDataLoaded} helpId={helpId}>
            <div className="ObjectEditor" style={{ width: editorWidth }}>
                {/* Header */}
                <div className="ObjectEditorHeader">
                    <ButtonContainer>
                        <SMCButton labelClassName={classes.labelColor} icon="delete" variant="ghost" disabled={isAdminReadOnly || !isPageDataLoaded} onClick={deleteButtonOnClick}>{t('SMC_GEN_DELETE')}</SMCButton>
                    {(displayChangeToGaAction) ?
                        (<>
                        <img src={lineSeparator} className={classes.separator} alt="separator"/>
                        <SMCButton  
                            labelClassName={classes.labelColor} 
                            icon="switch"
                            variant="ghost" 
                            disabled={isAdminReadOnly || !isPageDataLoaded} 
                            onClick={changeFirmwareOnAction}>{"Change to General Availability"}
                        </SMCButton>
                        </>) : null 
                    }
                        {(displayRecommendedAction) ?
                            (<>
                                <img src={lineSeparator} className={classes.separator} alt="separator"/>
                                <SMCButton
                                    labelClassName={classes.labelColor}
                                    icon="switch"
                                    variant="ghost"
                                    disabled={isAdminReadOnly || !isPageDataLoaded}
                                    onClick={changeFirmwareToRecommendedAction}>{"Set as recommended"}
                                </SMCButton>
                            </>) : null
                        }

                    </ButtonContainer>
                    <LabelContainer label="Name:" value={id} />
                </div>
                <SMCDisabledProvider forceDisable={isAdminReadOnly}>
                    <SMCEditor
                        editorPageData={editorPageData}
                        height={editorHeight}
                        width={editorWidth}
                        chosenPageId={chosenPageId}
                        onPageChange={onPageChange}
                        isPageLoaded={isPageDataLoaded}
                        isPageLoadFailed={isPageLoadFailed}
                        reloadData={reloadPageData}
                        pages={editorPages}
                    />
                </SMCDisabledProvider>
            </div>
        </SMCPage>
            <VerifyButtonsContainer
                disabled={isAdminReadOnly || !isPageDataLoaded}
                onClickSaveButton={saveButtonOnClick}
                onClickCancelButton={revertButtonOnClick}
            />
        </>
    );
}

const mapStateToProps = (state) => ({
    urlParams: state.router.location.search,
    isAdminReadOnly: isAdminReadOnlySelector(state)
});

const mapDispatchToProps = {
    navigateTo: push
}

export default connect(mapStateToProps, mapDispatchToProps)(FirmwareEditor);